import { DatePrecision } from "@/graphql/generated/operations";

export interface DateInstantInput {
  date: string;
  precision: DatePrecision;
}

// Genesis's DateInstant carries an explicit precision so YYYY-only and
// YYYY-MM values stay distinct from full YYYY-MM-DD. Inputs from
// MonthYearSelect arrive as 'YYYY-MM' (month-only) or 'YYYY' (year-only);
// inputs from native <input type="date"> arrive as 'YYYY-MM-DD'. The
// server requires a valid YYYY-MM-DD `date` field for all precisions, so
// month/year inputs are padded to the first of the month / first of the
// year - the precision tells the consumer to ignore the padded parts.
export function toDateInstant(value: string | null | undefined): DateInstantInput | null {
  const v = (value ?? "").trim();
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return { date: v, precision: DatePrecision.Day };
  if (/^\d{4}-\d{2}$/.test(v)) return { date: `${v}-01`, precision: DatePrecision.Month };
  if (/^\d{4}$/.test(v)) return { date: `${v}-01-01`, precision: DatePrecision.Year };
  return null;
}

/** The current calendar year — used by date tests and coarse year comparisons. */
export function currentYear(): number {
  return new Date().getFullYear();
}

/** Today as a local ISO {@code yyyy-MM-dd} string — the ceiling a manually entered
 *  date may not exceed. Local (not UTC) to match how the user reads "today". */
export function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Why a validator SEPARATE from toDateInstant: toDateInstant checks shape only
// (digit counts), so it happily accepts "6666" or "2020-13-99". The in-line
// merge review lets the user type dates as free text, so it needs to tell the
// user WHY a value is rejected before saving — a shape/calendar problem
// ("format") vs a date after today ("future"). toDateInstant is left
// unchanged (the profile editor's dropdowns can't produce bad values anyway).
export type DateFieldError = "format" | "future" | "range" | "beforeBirth";

const DATE_SHAPE = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/;

/** Validates a manually entered date string for the merge review. Returns null
 *  when it is acceptable — blank (a cleared / "ongoing" value) or a well-formed
 *  yyyy | yyyy-mm | yyyy-mm-dd with a real month/day that is not after `maxDate`
 *  (defaults to today). Otherwise returns why: "format" (wrong shape or impossible
 *  month/day) or "future" (the date falls after `maxDate`). The future check is a
 *  FULL-date comparison, so a date later than today WITHIN the current year is
 *  caught too — not just a later year. */
export function dateFieldError(value: string | null | undefined, maxDate: string = todayIso()): DateFieldError | null {
  const v = (value ?? "").trim();
  if (v === "") return null;
  const m = DATE_SHAPE.exec(v);
  if (!m) return "format";
  const year = Number(m[1]);
  const month = m[2] === undefined ? null : Number(m[2]);
  const day = m[3] === undefined ? null : Number(m[3]);
  if (year < 1) return "format";
  if (month !== null && (month < 1 || month > 12)) return "format";
  if (day !== null) {
    // Reject impossible days (e.g. 2021-02-30). A pure days-in-month check, NOT
    // `new Date(year, …)` — the Date constructor remaps years 0-99 to 1900-1999,
    // which would wrongly reject "0050-06-15".
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const daysInMonth = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day < 1 || day > daysInMonth[(month as number) - 1]!) return "format";
  }
  // Future: compare the full padded date (what the commit stores) to maxDate, so a
  // date later than today within the current year is caught, not only a later year.
  const instant = toDateInstant(v);
  if (instant && instant.date > maxDate) return "future";
  return null;
}

/** True when both `start` and `end` are present, well-formed dates and `start`
 *  falls strictly after `end` — the review's start≤end rule for experience and
 *  education rows. A blank/ongoing end ("present") or a malformed value is NOT a
 *  range error: a missing end means the row is ongoing, and bad shapes are the
 *  per-field validator's job. Compares the padded YYYY-MM-DD forms (exactly what
 *  the commit stores), so a coarser-precision end is treated as its start-of-
 *  period value — matching the date that actually reaches genesis. */
export function isStartAfterEnd(start: string | null | undefined, end: string | null | undefined): boolean {
  const s = toDateInstant(start);
  const e = toDateInstant(end);
  if (!s || !e) return false;
  return s.date > e.date;
}

/** True when both `date` and `dob` are well-formed and `date` falls strictly before
 *  `dob` — an entry (experience/education start, certificate issue) that predates the
 *  person's birth. A blank/ongoing or malformed value on either side is NOT a birth
 *  violation (a missing DOB means no check; bad shapes are the per-field validator's
 *  job). Compares padded YYYY-MM-DD forms, matching the committed values. */
export function isBeforeBirthDate(date: string | null | undefined, dob: string | null | undefined): boolean {
  const d = toDateInstant(date);
  const b = toDateInstant(dob);
  if (!d || !b) return false;
  return d.date < b.date;
}
